import { CdkVirtualScrollableElement } from '@angular/cdk/scrolling';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  inject,
  input,
  linkedSignal,
  output
} from '@angular/core';

import { isNil } from '@gfe/core/utils/shared-utils';
import { SummaryItem } from '@gfe/features/components/entities/summary/summary-item';

import {
  ReferenceNameLookup,
  TableContentComponent
} from '@shared/components/table/table-content/table-content.component';
import { TableHeaderComponent } from '@shared/components/table/table-header/table-header.component';
import { TableSummaryComponent } from '@shared/components/table/table-summary/table-summary.component';
import {
  CellOptions,
  GoToReference,
  RowEvent,
  ShowDetails,
  SortDirection,
  SortState,
  TableAction,
  TableColumn,
  TableConfig,
  TableRowError
} from '@shared/components/table/table.model';
import { DropdownItem } from '@shared/models/dropdown-item.model';
import { TableCustomisationService } from '@shared/services/table-customisation/table-customisation.service';

/**
 * Width of a leading (non-data) grid track. Declared once on the `app-table` host so the
 * tracks reserved here and the `.table-frozen-cell` sticky offsets cannot drift apart —
 * a mismatch stacks the checkbox on top of the row number instead of beside it.
 */
const LEADING_TRACK = 'var(--table-leading-track)';

@Component({
  selector: 'app-table',
  imports: [TableSummaryComponent, TableHeaderComponent, TableContentComponent],
  /* Takes the vertical scrolling off the CDK viewport and onto this host, making the host
     the table's single scrollport in both axes. That is what lets the header, the summary
     and the frozen leading columns all pin with plain `position: sticky`, on the
     compositor — see .table-frozen-cell in styles.css. */
  hostDirectives: [CdkVirtualScrollableElement],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './table.component.html',
  styles: `
    :host {
      display: block;
      width: 100%;
      height: 100%;
      container-type: inline-size;
      /* The table's one scrollport, in both axes. The cdk-virtual-scrollable class would
         set this too, but the sticky header, summary and frozen columns all resolve
         against it, so it is stated here rather than inherited from a CDK class. */
      overflow: auto;
      /* Width of each leading (non-data) track, shared with getGridTemplateColumns and
         with the .table-frozen-cell offsets. */
      --table-leading-track: 38px;
    }
  `
})
export class TableComponent<T> {
  data = input<T[]>([]);
  allColumns = input<TableColumn<T>[]>([]);
  /** Entity whose rows these are, so a reference cell editor can scope its presets. */
  ownerEntityType = input<string>('');
  actions = input<TableAction<T>[]>([]);
  selected = input<Set<T>>(new Set<T>());
  errors = input<TableRowError<T>[]>([]);
  pendingRowUids = input<ReadonlySet<string>>(new Set<string>());
  cellOptions = input<CellOptions<T>[]>([]);
  referenceNames = input<ReferenceNameLookup | undefined>(undefined);
  tableId = input.required<string>();
  summaryItems = input<SummaryItem<T>[] | undefined>([]);
  /**
   * Seeds the sort from outside, for callers restoring a saved view. Every new array
   * replaces the user's own sorting, so pass a fresh one only when the order is meant to
   * change — re-emitting the same content with a new reference would undo a column the
   * user just clicked.
   */
  initialSort = input<SortState<T>[]>([]);
  /**
   * Whether the table re-sorts its rows by its header. Off for a caller whose rows arrive in an
   * order the header cannot show, such as a sort on a related entity ahead of a column.
   */
  clientSort = input(true);

  config = input<TableConfig<T>>({
    striped: false,
    bordered: false,
    editable: {
      inline: false,
      newTab: false
    },
    rowNumbers: false,
    columnTooltips: false,
    horizontalScroll: false,
    columnActionSettings: {
      sortable: true,
      filterable: false,
      groupable: false,
      movable: false,
      resizeable: false,
      hideable: false
    },
    selection: {
      enabled: false,
      highlightSelected: false,
      mode: 'checkbox'
    }
  });

  rowClick = output<T>();
  sortChange = output<SortState<T>[]>();
  indexMap = output<number[]>();
  rowEdit = output<RowEvent<T>>();
  goToReference = output<GoToReference<T>>();
  showDetails = output<ShowDetails<T>>();
  addFilterOn = output<keyof T>();
  removeFilterOn = output<keyof T>();
  listItemClick = output<{ row: T; column: keyof T; item: unknown }>();
  keyValueClick = output<{ row: T; column: keyof T; key: string; value: unknown }>();

  /** Writable so header clicks can drive it, but reset whenever `initialSort` changes. */
  sortState = linkedSignal(() => this.initialSort());

  sortedData = computed(() => {
    const data = this.data();
    const sortState = this.sortState();

    if (!this.clientSort() || sortState.length <= 0) {
      return data;
    }

    return [...data].sort((a, b) => {
      for (const sort of sortState) {
        const aVal = a[sort.column!];
        const bVal = b[sort.column!];

        // Handle null/undefined values
        if (isNil(aVal) && isNil(bVal)) return 0;
        if (isNil(aVal)) return 1;
        if (isNil(bVal)) return -1;

        const column = this.columns().find((col) => col.key === sort.column);
        const comparison = this.compareValues(aVal, bVal, column?.type ?? 'string');
        if (comparison !== 0) {
          return sort.direction === 'asc' ? comparison : -comparison;
        }
      }

      // If no column sorts the values, we return 0, because the order is equivalent
      return 0;
    });
  });

  columns = computed(() => {
    const id = this.tableId();
    const allColumns = this.allColumns();

    if (id && allColumns.length > 0) {
      const visibleColumns = this.tableCustomisationService.getVisibleColumns(id)();
      return visibleColumns.length > 0 ? visibleColumns : allColumns;
    }

    return allColumns;
  });

  private readonly tableCustomisationService = inject(TableCustomisationService);

  constructor() {
    effect(() => {
      this.sortChange.emit(this.sortState());
    });

    // The arrow a header draws is a field on the column object, so it has to be derived
    // from the sort rather than written where the sort is: a seeded sort never goes
    // through onSort, and `columns()` hands out fresh objects on every recompute.
    effect(() => {
      const directions = new Map(
        this.sortState().map((sort) => [sort.column, sort.direction])
      );
      for (const column of this.columns()) {
        column.sortDirection = directions.get(column.key);
      }
    });

    effect(() => {
      const map = this.sortedData().map((row) => this.data().indexOf(row));
      this.indexMap.emit(map);
    });

    effect(() => {
      const id = this.tableId();
      const columns = this.initColumns(this.allColumns());

      if (id && columns.length > 0) {
        this.tableCustomisationService.initTableConfig<T>(id, columns);
      }
    });
  }

  isRowSelected(row: T): boolean {
    if (!this.config().selection?.enabled || !this.selected()) {
      return false;
    }

    return this.selected().has(row);
  }

  getHiddenColumns(): DropdownItem[] {
    const id = this.tableId();
    if (!id) return [];

    const allCols = this.allColumns();
    const visibleCols = this.columns();
    const hiddenCols = allCols.filter(
      (col) => !visibleCols.some((vCol) => vCol.key === col.key)
    );
    return hiddenCols.map((col) => ({
      label: col.label,
      action: () => {
        this.tableCustomisationService.toggleColumnVisibility(id, String(col.key));
      }
    }));
  }

  // Sorting functionality
  onSort(column: TableColumn<T>, direction: SortDirection) {
    if (!column.config?.sortable || !this.config().columnActionSettings?.sortable) return;

    this.sortState.update((cur) => {
      const newSort = cur.filter((val) => val.column !== column.key);
      newSort.push({ column: column.key, direction });
      return newSort;
    });
  }

  removeSort(column: TableColumn<T>) {
    this.sortState.update((cur) => {
      return cur.filter((val) => val.column !== column.key);
    });
  }

  private initColumns(columns: TableColumn<T>[]): TableColumn<T>[] {
    return columns.map((col, index) => ({
      ...col,
      config: {
        sortable: col.config?.sortable ?? false,
        filterable: col.config?.filterable ?? false,
        groupable: col.config?.groupable ?? false,
        hideable: col.config?.hideable ?? false,
        orderable: col.config?.orderable ?? false,
        resizeable: col.config?.resizeable ?? false
      },
      order: col.order ?? index,
      visible: col.visible ?? true,
      width: col.width ?? null
    }));
  }

  isAnySelectedRow(): boolean {
    if (this.config().selection?.enabled) {
      if (this.selected()) {
        return this.selected().size > 0;
      }
    }
    return false;
  }

  isAllRowSelected(): boolean {
    if (this.config().selection?.enabled && this.config().selection?.multiple) {
      if (this.selected()) {
        return this.selected().size === this.data().length && this.data().length > 0;
      }
    }
    return false;
  }

  toggleSelection() {
    if (!this.config().selection?.enabled || !this.config().selection?.multiple) {
      return;
    }

    const selectedSet = this.selected();
    const executeIfNotExists =
      selectedSet?.size === 0 ||
      (!isNil(selectedSet) && selectedSet?.size < this.data().length);

    const shouldExecute = executeIfNotExists
      ? (row: T) => !selectedSet?.has(row)
      : (row: T) => selectedSet?.has(row);

    this.data().forEach((row) => {
      if (shouldExecute(row)) {
        this.config().selection?.action?.(row);
      }
    });
  }

  resetSortState() {
    this.sortState.update(() => []);
  }

  private compareValues(a: unknown, b: unknown, type: TableColumn<T>['type']): number {
    switch (type) {
      case 'number':
      case 'boolean':
        return Number(a) - Number(b);
      case 'date':
        return (
          new Date(a as string | number | Date).getTime() -
          new Date(b as string | number | Date).getTime()
        );
      default:
        return String(a).localeCompare(String(b));
    }
  }

  // Summary footer methods
  protected hasSummaryData(): boolean {
    const items = this.summaryItems();
    return !isNil(items) && items.length > 0;
  }

  protected getTableStyles(): Record<string, string> {
    return {
      width: '100%',
      display: 'grid',
      'grid-template-columns': this.getGridTemplateColumns()
    };
  }

  /**
   * Leading columns the rows draw before the first data column. It has to match the tracks
   * `getGridTemplateColumns` reserves for them exactly, or the summary's placeholders push
   * its cells out of line with the header and the body.
   */
  protected getUnusedLeftColumnsCount(): number {
    const config = this.config();
    let count = 0;

    if (config.rowNumbers) {
      count++;
    }

    // Click-mode selection renders no cell of its own.
    if (config.selection?.enabled && config.selection.mode === 'checkbox') {
      count++;
    }

    return count;
  }

  protected getUnusedRightColumnsCount(): number {
    let count = 0;

    if (this.actions() && this.actions().length > 0) {
      count++;
    }

    return count;
  }

  protected getGridTemplateColumns(): string {
    const columns = this.columns();
    const config = this.config();
    const parts: string[] = [];
    const hasFixedColumns = columns.some((col) => col.width);

    // Order matters: the rows draw the row number before the checkbox, so reserving the
    // tracks the other way round puts each leading cell in the other one's track.
    if (config.rowNumbers) {
      parts.push(LEADING_TRACK);
    }

    if (config.selection?.enabled && config.selection.mode === 'checkbox') {
      parts.push(LEADING_TRACK);
    }

    for (let i = 0; i < columns.length; i++) {
      const column = columns[i];
      const isLastColumn = i === columns.length - 1;

      if (column.width) {
        if (isLastColumn && hasFixedColumns) {
          parts.push(`minmax(${column.width}px, 1fr)`);
        } else {
          parts.push(`${column.width}px`);
        }
      } else {
        parts.push('minmax(100px, 1fr)');
      }
    }

    if (this.actions() && this.actions().length > 0) {
      parts.push(`${this.actions().length * 32}px`);
    }

    if (this.getHiddenColumns().length > 0) {
      parts.push('38px');
    }

    return parts.join(' ');
  }
}
